import React, { useCallback, useState } from "react";

import { Plus } from "icons";
import { useParams } from "react-router-dom";

import businessTermsExploreApi from "apis/businessTermsExplore";
import dataSourcesApi from "apis/dataSource";
import { Button, PageLoader, Typography } from "clearsense-ui/components";
import ExploreContainer from "components/Common/ExploreContainer";
import FilterHeader from "components/Common/FilterHeader";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import { useUserState } from "contexts/user";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";
import { getPermissions } from "utils/roleBasedAccess";

import AssociateDataSource from "./AssociateDataSource";
import DataSourceTable from "./Table";

import { DATA_SOURCE_COLUMNS } from "../../constants/dataSourcesExplore";
import BusinessGlossarySummaryCard from "../BusinessGlossarySummaryCard";
import DeleteDataSources from "../DeleteAlert";

const DataSources = ({ features, businessGlossary, fetchBusinessGlossary }) => {
  const user = useUserState();
  const userPermissions = getPermissions(features, user?.role);
  const { businessGlossaryId } = useParams();

  const [checkedRows, setCheckedRows] = useState([]);
  const [isAssociateDataSourceOpen, setIsAssociateDataSourceOpen] =
    useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [dataSources, setDataSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDataSources = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const { data } = await businessTermsExploreApi.fetchDataSources(
        businessGlossaryId,
        currentPage,
        pageSize,
        search
      );
      setDataSources(data?.data_sources);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteRecords = async () => {
    try {
      setIsLoading(true);
      const checkedRowIds = checkedRows.map(checkedRow => checkedRow.id);
      await dataSourcesApi.delete(checkedRowIds);
      fetchDataSources();
      fetchBusinessGlossary();
      filterParams.setCurrentPage(1);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterParams = useSearchAndPaginate(fetchDataSources);

  const columnHeaderSelector = useColumnHeaderSelector(DATA_SOURCE_COLUMNS);

  return (
    <>
      <ExploreContainer>
        <div className="flex justify-between">
          <Typography style="h3" type="semibold">
            Data Sources using {businessGlossary?.name} (
            {dataSources.data_source_count})
          </Typography>
          <Typography style="h3" type="semibold">
            Summary
          </Typography>
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="associate"
          >
            <Button
              label="Associate Data Sources"
              icon={Plus}
              onClick={() => setIsAssociateDataSourceOpen(true)}
              data-cy="associate-data-source-button"
            />
          </PermissibleWrapper>
        </div>
        <BusinessGlossarySummaryCard businessGlossary={businessGlossary} />
        <FilterHeader
          entity="Data Sources"
          filterParams={filterParams}
          columnHeaderSelector={columnHeaderSelector}
          checkedRows={checkedRows}
          userPermissions={userPermissions}
          count={dataSources.data_source_count}
          setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        />
        {isLoading ? (
          <PageLoader />
        ) : (
          <DataSourceTable
            totalRecordsCount={dataSources.data_source_count}
            filterParams={filterParams}
            dataSourceColumnData={dataSources?.data_sources}
            userPermissions={userPermissions}
            columns={columnHeaderSelector.columns}
            setCheckedRows={setCheckedRows}
            checkedRows={checkedRows}
            setIsAssociateDataSourceOpen={setIsAssociateDataSourceOpen}
          />
        )}
      </ExploreContainer>
      <DeleteDataSources
        entityName="Data Source"
        deleteRecords={deleteRecords}
        isDeleteAlertOpen={isDeleteAlertOpen}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        checkedRows={checkedRows}
        setCheckedRows={setCheckedRows}
      />
      <AssociateDataSource
        isPaneOpen={isAssociateDataSourceOpen}
        setIsPaneOpen={setIsAssociateDataSourceOpen}
        businessGlossary={businessGlossary}
        fetchBusinessGlossary={fetchBusinessGlossary}
        fetchDataSources={fetchDataSources}
      />
    </>
  );
};

export default DataSources;
